/**
 * Breadcrumb Utilities
 * Generate breadcrumb trails from route hierarchy
 *
 * Breadcrumbs are built by:
 * 1. Finding the current route
 * 2. Walking up the parent chain via breadcrumbParent
 * 3. Resolving dynamic labels with route params
 */

import { routeRegistry } from './registry';
import type { RouteConfig, RouteParams, BreadcrumbItem } from './types';
import { Home } from 'lucide-react';

// ============================================================================
// Breadcrumb Generation
// ============================================================================

/**
 * Generate breadcrumbs for a given path
 */
export async function generateBreadcrumbs(
  pathname: string,
  params: RouteParams
): Promise<BreadcrumbItem[]> {
  const route = routeRegistry.getByPath(pathname);

  // No route found or breadcrumbs hidden
  if (!route || route.hideBreadcrumb) {
    return [];
  }

  // Build the chain from root to current
  const chain = await buildBreadcrumbChain(route, params);

  return chain;
}

/**
 * Build breadcrumb chain by walking up parent hierarchy
 */
async function buildBreadcrumbChain(
  route: RouteConfig,
  params: RouteParams
): Promise<BreadcrumbItem[]> {
  const chain: BreadcrumbItem[] = [];
  const visited = new Set<string>(); // Prevent infinite loops

  let currentRoute: RouteConfig | undefined = route;

  while (currentRoute && !visited.has(currentRoute.id)) {
    visited.add(currentRoute.id);

    if (!currentRoute.hideBreadcrumb) {
      // Resolve label
      const label = await resolveLabel(currentRoute, params);

      // Build href (substitute params in path)
      const href = substitutePathParams(currentRoute.path, params.params);

      chain.unshift({
        id: currentRoute.id,
        label,
        href,
        current: currentRoute.id === route.id,
      });
    }

    // Move to parent
    if (currentRoute.breadcrumbParent) {
      currentRoute = routeRegistry.getById(currentRoute.breadcrumbParent);
    } else {
      currentRoute = undefined;
    }
  }

  return chain;
}

/**
 * Resolve breadcrumb label (handles static and dynamic labels)
 */
async function resolveLabel(
  route: RouteConfig,
  params: RouteParams
): Promise<string> {
  // Use breadcrumbLabel if defined
  if (route.breadcrumbLabel) {
    if (typeof route.breadcrumbLabel === 'function') {
      return await route.breadcrumbLabel(params);
    }
    return route.breadcrumbLabel;
  }

  // Fall back to title
  if (typeof route.title === 'function') {
    return await route.title(params);
  }

  return route.title;
}

/**
 * Substitute dynamic segments in path with actual values
 */
export function substitutePathParams(
  pattern: string,
  params: Record<string, string>
): string {
  let path = pattern;

  // Replace [param] with actual value
  for (const [key, value] of Object.entries(params)) {
    path = path.replace(`[${key}]`, value);
    path = path.replace(`[...${key}]`, value);
  }

  // Clean up any unsubstituted params (shouldn't happen in practice)
  path = path.replace(/\[[^\]]+\]/g, '');

  return path;
}

// ============================================================================
// Breadcrumb Display Helpers
// ============================================================================

/**
 * Format breadcrumb for display (truncate long labels)
 */
export function formatBreadcrumbLabel(label: string, maxLength = 30): string {
  if (label.length <= maxLength) return label;
  return `${label.slice(0, maxLength - 3)}...`;
}

/**
 * Get separator for breadcrumb display
 */
export function getBreadcrumbSeparator(): string {
  return '/';
}

/**
 * Check if breadcrumbs should be shown for a path
 */
export function shouldShowBreadcrumbs(pathname: string): boolean {
  const route = routeRegistry.getByPath(pathname);

  if (!route) return false;
  if (route.hideBreadcrumb) return false;

  // Don't show for root paths
  if (pathname === '/') return false;
  if (pathname === '/dashboard') return false;

  return true;
}

// ============================================================================
// Structured Data (SEO)
// ============================================================================

/**
 * Generate JSON-LD breadcrumb structured data for SEO
 */
export function generateBreadcrumbJsonLd(
  breadcrumbs: BreadcrumbItem[],
  baseUrl: string
): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.label,
      item: crumb.current ? undefined : `${baseUrl}${crumb.href}`,
    })),
  };
}
